import PropTypes from 'prop-types';
import { Component, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { List } from 'foundations-components/transitional/components/List';
import { ResultsMessage } from 'foundations-components/transitional/components/ResultsMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TeamOptionType } from '../types/OptionTypes';
import { ListsIcon } from 'foundations-assets/icons/components/ListsIcon';
import { useTheme } from 'foundations-theming/utils';
import { getSortedFlattenedTeamsList } from '../utils/TeamUtils';
import NestingStructureItemWrapper from './common/NestingStructureItemWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FALLBACK_CHECKBOX_WIDTH = 24;
const LINE_WIDTH = 1;
const TeamCheckbox = ({
  id,
  name,
  depth,
  checked,
  onRemoveTeam,
  onAddTeam,
  disabled,
  previousTeamDepth
}) => {
  const {
    space
  } = useTheme();
  const checkboxRef = useRef(null);
  const [checkboxWidth, setCheckboxWidth] = useState(undefined);
  useLayoutEffect(() => {
    if (checkboxRef.current) {
      const checkboxSpan = checkboxRef.current.querySelector('span[class^=Checkbox__CheckboxIndicator]');
      if (checkboxSpan) {
        setCheckboxWidth(Math.round(checkboxSpan.getBoundingClientRect().width));
      }
    }
  }, [checkboxRef]);
  const indentSize = useMemo(() => {
    return checkboxWidth ? checkboxWidth + parseInt(space[100], 10) - LINE_WIDTH : FALLBACK_CHECKBOX_WIDTH;
  }, [checkboxWidth, space]);
  const isMac = navigator.userAgent.includes('Mac');
  const tooltipText = useMemo(() => {
    if (isMac) {
      return 'Click to select this Team and all its descendants. \n\nHold ⌘ and click to select this Team only.'; // Mac command symbol
    } else {
      return 'Click to select this Team and all its descendants. \n\nHold Ctrl and click to select this Team only.'; // Windows/Linux
    }
  }, [isMac]);
  return /*#__PURE__*/_jsx(NestingStructureItemWrapper, {
    depth: depth,
    previousDepth: previousTeamDepth,
    indentSize: indentSize,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "start",
      gap: "200",
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: tooltipText,
        placement: "top",
        delay: 2000,
        delayOnHold: 700,
        textWrap: "pretty"
        // @ts-expect-error - valid style, applies correctly, types flag it incorrectly
        ,
        innerCssStyles: {
          whiteSpace: 'pre-wrap'
        },
        children: /*#__PURE__*/_jsx(Checkbox, {
          ref: checkboxRef,
          value: id,
          checked: checked,
          paddingInlineEnd: "100",
          inlineSize: "fit-content",
          alignment: "center",
          justifyContent: "center",
          disabled: disabled,
          "data-test-id": "team-checkbox",
          onChange: e => {
            if (checked) {
              onRemoveTeam(id);
            } else {
              if (isMac && e.nativeEvent.metaKey) {
                onAddTeam(id, false);
              } else if (!isMac && e.nativeEvent.ctrlKey) {
                onAddTeam(id, false);
              } else {
                onAddTeam(id, true);
              }
            }
          },
          children: /*#__PURE__*/_jsxs(Flex, {
            "data-test-id": `assign-team-checkbox-${id}`,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "start",
            gap: "200",
            children: [/*#__PURE__*/_jsx(ListsIcon, {}), /*#__PURE__*/_jsx(Text, {
              children: name
            })]
          })
        })
      })
    })
  });
};
class SelectableTeamsList extends Component {
  constructor(...args) {
    super(...args);
    this.renderTeamCheckbox = ({
      depth,
      id,
      name,
      previousTeamDepth
    }) => {
      const {
        selectedTeamIds = [],
        onAddTeam,
        onRemoveTeam,
        rootLevelTeamIds,
        userHasTeamAssignmentScope,
        allowedTeamIds,
        DisallowedTeamFormattedMessage
      } = this.props;
      const checked = selectedTeamIds.includes(id);
      const isRootLevelTeam = rootLevelTeamIds.includes(id);
      const isDisabled = isRootLevelTeam && checked && !userHasTeamAssignmentScope;
      const isTeamDisallowed = allowedTeamIds && !allowedTeamIds.includes(id);
      const key = `${id}-team-checkbox`;
      const checkbox = /*#__PURE__*/_jsx(TeamCheckbox, {
        id: id,
        name: name,
        depth: depth,
        checked: checked,
        disabled: isDisabled || isTeamDisallowed,
        onAddTeam: onAddTeam,
        onRemoveTeam: onRemoveTeam,
        previousTeamDepth: previousTeamDepth
      }, key);
      if (isDisabled || isTeamDisallowed) {
        return /*#__PURE__*/_jsx(Tooltip, {
          placement: "left",
          title: isTeamDisallowed ? DisallowedTeamFormattedMessage ? /*#__PURE__*/_jsx(DisallowedTeamFormattedMessage, {
            teamId: id,
            teamName: name
          }) : null : /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'ui-asset-management-lib.tooltips.disabledTeamTooltip'
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: checkbox
          })
        }, key);
      }
      return checkbox;
    };
  }
  render() {
    const {
      teams
    } = this.props;
    const teamOptions = getSortedFlattenedTeamsList(teams);
    if (!teamOptions.length) {
      return /*#__PURE__*/_jsx(ResultsMessage, {
        illustration: "empty-state-charts",
        illustrationProps: {
          width: 187
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.labels.noTeams"
        })
      });
    }
    return /*#__PURE__*/_jsx(List, {
      children: teamOptions.map((team, i) => {
        var _teamOptions$depth;
        return this.renderTeamCheckbox({
          depth: team.depth || 0,
          id: team.id,
          name: team.name,
          previousTeamDepth: i > 0 ? (_teamOptions$depth = teamOptions[i - 1].depth) !== null && _teamOptions$depth !== void 0 ? _teamOptions$depth : 0 : 0
        });
      })
    });
  }
}
SelectableTeamsList.propTypes = {
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  selectedTeamIds: PropTypes.arrayOf(PropTypes.number),
  onAddTeam: PropTypes.func.isRequired,
  onRemoveTeam: PropTypes.func.isRequired,
  rootLevelTeamIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  userHasTeamAssignmentScope: PropTypes.bool.isRequired,
  allowedTeamIds: PropTypes.arrayOf(PropTypes.number),
  DisallowedTeamFormattedMessage: PropTypes.func
};
export default SelectableTeamsList;